import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

// VRG-140 Slice 3 (VERA-891): possession-bucketed company inventory — the
// Incoming (in_transit) / Available (receipt-scanned) tag lists behind the new
// Inventory sidebar surface. See GetCompanyInventoryUseCase (VRG-140 Slice 1).

export interface CompanyInventoryTagItem {
  id: string
  uid: string
  productTitle: string | null
  productImage: string | null
  /**
   * VRG-345 — null for a company-direct tag (one a Vera admin handed straight to the brand, with
   * no package behind it). `packageName` / `shopifyOrderGid` / `tagProductId` are null with it.
   */
  packageId: string | null
  packageName: string | null
  shopifyOrderGid: string | null
  possessionState: 'at_vera' | 'in_transit' | 'available'
  /**
   * The tag SKU this chip is, as the id the tag-inventory overview keys rows on. Null when
   * the package carries no tag-shop association, and always null for a company-direct tag
   * (there is no package to resolve through). Resolved server-side through the package —
   * the tag's own `product_sku`/`product_title` hold the VARIANT's sku/title and are null on
   * older tags, so neither can key a per-type aggregate.
   */
  tagProductId: string | null
}

export interface CompanyInventoryResponse {
  /** VERA-455: Ordered — paid for, still with Vera. No longer excluded from the response. */
  atVera: CompanyInventoryTagItem[]
  incoming: CompanyInventoryTagItem[]
  available: CompanyInventoryTagItem[]
  counts: { atVera: number; incoming: number; available: number }
}

const EMPTY: CompanyInventoryResponse = {
  atVera: [],
  incoming: [],
  available: [],
  counts: { atVera: 0, incoming: 0, available: 0 },
}

export const COMPANY_INVENTORY_QUERY_KEY = ['inventory', 'possession'] as const

export const useCompanyInventory = () => {
  const query = useQuery({
    queryKey: COMPANY_INVENTORY_QUERY_KEY,
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/shop/tags/inventory/possession',
      })
      return response.data as CompanyInventoryResponse
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })

  return {
    data: query.data ?? EMPTY,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * VRG-140 Slice 3: receipt-scan a package — flips its tags Incoming -> Available
 * for the authenticated company.
 *
 * Invalidates BOTH possession queries on success: the raw bucket query, and the tag-inventory
 * OVERVIEW, whose per-SKU Incoming / On hand columns are derived from the same possession
 * state (VERA-455). Missing the second is silent — the receipt succeeds, the toast fires, and
 * the table the user is looking at keeps showing the pre-receipt counts until a reload.
 */
export const useReceivePackage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ packageId }: { packageId: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/shop/tags/inventory/receive',
        data: { packageId },
      })
      return response.data as { packageId: string; updated: number }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANY_INVENTORY_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['tag-inventory', 'overview'] })
    },
  })
}
